import { API_URL, SAML_URL, STATIC_URL, WEBSOCKET_URL } from
  'assets/js/api/environment';

// urls is a list of all the extensions for the base URL used to do GET for
// POST commands in the API. Commands with multiple arguments that might be
// ambiguous are described in comments below, while the other commands should
// be self-describing.
//
// You might be wondering why we constantly manually construct GETs or POSTs
// with parameters throughout the codebase. The reason is that the API is
// massively inconsistent. For example, the TAGS GET API requires the site ID in
// the query parameters, while the training GET API requires that the name of
// the site be given in the request route. This makes it incredibly difficult
// to create a 'one size fits all' approach to getters and setters.
export const urls = {
  // ---------------------------------------------------------------------------
  // Login
  // ---------------------------------------------------------------------------
  API: API_URL,
  CREATE_USER: `${API_URL}/users`,
  IDP_SEARCH: (idp, q) => `${SAML_URL}/searchidps/${idp}?query=${q}`, // GET
  INITIATE_PASSWORD_RESET: `${API_URL}/initiatepasswordreset`,
  LOGIN: `${API_URL}/session`,
  LOGOUT: `${API_URL}/logout`,
  USER_SESSION: (userId) => `${API_URL}/session/${userId}`,
  PASSWORD_RESET: `${API_URL}/initiatepasswordreset`,
  SECOND_FACTOR_CHECK: `${API_URL}/2fa/check`,
  WEBAUTHN_AUTH_CHALLENGE: `${API_URL}/2fa/webauthn/challenge`,

  // ---------------------------------------------------------------------------
  // Super Admin
  // ---------------------------------------------------------------------------
  ADMIN_ANNOUNCEMENTS: `${API_URL}/admin/announcements`,
  ADMIN_ARCHIVE_ALL_RESOURCES: (id) =>
    `${API_URL}/admin/sites/${id}/resources/archive`,
  ADMIN_ACCESS_URLS: `${API_URL}/admin/accessurls`,
  ADMIN_ACCESS_URLS_EDIT: (id) => `${API_URL}/admin/accessurls/${id}`,
  ADMIN_CURRENT_STATISTICS: `${API_URL}/admin/statistics/current`,
  ADMIN_CUSTOMER: (id) => `${API_URL}/admin/customers/${id}`,
  ADMIN_CUSTOMER_FILE: (id) => `${API_URL}/admin/customers/${id}/file`,
  ADMIN_CUSTOMER_FILE_DOWNLOAD: (id, fileId) =>
    `${API_URL}/admin/customers/${id}/file/${fileId}`,
  ADMIN_CUSTOMER_FILE_EDIT: (id, fileId) =>
    `${API_URL}/admin/customers/${id}/file/${fileId}`,
  ADMIN_CUSTOMER_LOG: (id) => `${API_URL}/admin/customers/${id}/log`,
  ADMIN_CUSTOMER_LOG_EDIT: (id, logId) =>
    `${API_URL}/admin/customers/${id}/log/${logId}`,
  ADMIN_CUSTOMERS: `${API_URL}/admin/customers`,
  ADMIN_CUSTOMER_LINK: (id) => `${API_URL}/admin/customers/${id}/link`,
  ADMIN_EMAIL_STATUS: `${API_URL}/admin/email/status`,
  ADMIN_EMAIL_HALT: `${API_URL}/admin/email/halt`,
  ADMIN_EMAIL_ALLOW: `${API_URL}/admin/email/allow`,
  ADMIN_EMBEDS_ACTIVE: `${API_URL}/admin/embeds/active`,
  ADMIN_INTEGRATIONS_SLACK: `${API_URL}/admin/integrations/slack`,
  ADMIN_IP_AUDIT: `${API_URL}/admin/ip/audit`,
  ADMIN_IP_BAN: `${API_URL}/admin/ip/ban`,
  ADMIN_IP_UNBAN: `${API_URL}/admin/ip/unban`,
  ADMIN_IP_PROBLEMS: `${API_URL}/admin/ip/problems`,
  ADMIN_KIOSKS_ACTIVE: `${API_URL}/admin/kiosks/active`,
  ADMIN_LOGS_USER: (id) => `${API_URL}/admin/logs/user/${id}`,
  ADMIN_LOGS: `${API_URL}/admin/logs`,
  ADMIN_LOGS_CODES: `${API_URL}/admin/logs/codes`,
  ADMIN_ORPHAN_STRIPE_SUBS: `${API_URL}/admin/orphan_stripe_subscriptions`,
  ADMIN_PLANS: `${API_URL}/admin/plans`,
  ADMIN_PORTAL: (id) => `${API_URL}/admin/portals/${id}`,
  ADMIN_PORTALS: `${API_URL}/admin/portals`,
  ADMIN_PORTAL_DELETE: (id) => `${API_URL}/portals/${id}/delete`,
  ADMIN_PORTAL_PLAN: (id) => `${API_URL}/admin/portals/${id}/plan`,
  ADMIN_PORTAL_USER_ADD: (id) => `${API_URL}/admin/portals/${id}/adduser`,
  ADMIN_PORTAL_USER_REM: (id) => `${API_URL}/admin/portals/${id}/remuser`,
  ADMIN_RELEASE_NOTES: `${API_URL}/admin/releasenotes`,
  ADMIN_REPORTS_CUSTOM: (id) => `${API_URL}/reports/custom/${id}`, // POST
  ADMIN_REPORTS_CUSTOM_AVAILABLE: `${API_URL}/reports/custom/list`, // GET
  ADMIN_REPORTS_CUSTOM_CREATE: `${API_URL}/reports/custom`, // POST
  ADMIN_REPORTS_CUSTOM_DELETE: (id) => `${API_URL}/reports/custom/${id}/delete`,
  ADMIN_REPORTS_CUSTOM_UPLOAD: (id) =>
    `${API_URL}/reports/custom/${id}/template_file`, // POST
  ADMIN_SCRIPTS: `${API_URL}/admin/scripts`,
  ADMIN_SITE: (id) => `${API_URL}/admin/sites/${id}`,
  ADMIN_SITE_APPROVE: (id) => `${API_URL}/admin/site/${id}/approve`,
  ADMIN_SITE_DELETE: (id) => `${API_URL}/admin/site/${id}/delete`,
  ADMIN_SITE_UNDELETE: (id) => `${API_URL}/admin/site/${id}/undelete`,
  ADMIN_SITE_OBFUSCATE: (id) => `${API_URL}/admin/site/${id}/obfuscate`,
  ADMIN_SITE_PLAN: (id) => `${API_URL}/admin/site/${id}/plan`,
  ADMIN_SITE_USER_ADD: (id) => `${API_URL}/admin/sites/${id}/adduser`,
  ADMIN_SITE_USER_REM: (id) => `${API_URL}/admin/sites/${id}/remuser`,
  ADMIN_SITES: `${API_URL}/admin/sites`,
  ADMIN_SITES_CSV: `${API_URL}/admin/sitescsv`,
  ADMIN_THEME_SITE: (id) => `${API_URL}/admin/themes/${id}/sites`,
  ADMIN_THEMES: `${API_URL}/admin/themes`,
  ADMIN_USER: (id) => `${API_URL}/admin/users/${id}`,
  ADMIN_USER_BLOCKED: (id) => `${API_URL}/admin/user/${id}/blocked`,
  ADMIN_USER_OBFUSCATE: (id) => `${API_URL}/admin/user/${id}/obfuscate`,
  ADMIN_USER_NOTIFY_OF_OBFUSCATION: (id) =>
    `${API_URL}/admin/user/${id}/obfuscate/notify`,
  ADMIN_USER_SET_EMAIL: (id) => `${API_URL}/admin/user/${id}/setemail`,
  ADMIN_USER_PASSWORD_SET: (id) => `${API_URL}/admin/user/${id}/password`,
  ADMIN_USER_UNRESTRICTED_SITES: (id) =>
    `${API_URL}/admin/user/${id}/unrestricted_sites`,
  ADMIN_USERS: `${API_URL}/admin/users`,
  ADMIN_USERS_ACTIVE_MODS: `${API_URL}/admin/users/moderators/active`,
  ADMIN_UI_VERSIONS: `${API_URL}/admin/ui/versions`,

  // ---------------------------------------------------------------------------
  // Misc
  // ---------------------------------------------------------------------------
  CONTACT_QRESERVE: `${API_URL}/contactus`,
  VERSION: `${STATIC_URL}/version/version.json`,
  VERSION_CHECK: (version) => `${API_URL}/ui/version/check?v=${version}`,

  // ---------------------------------------------------------------------------
  // Statistic Module
  // ---------------------------------------------------------------------------
  STATISTIC: (statistic) => `${API_URL}/statistic?statistic=${statistic}`,

  // ---------------------------------------------------------------------------
  // Portico
  // ---------------------------------------------------------------------------
  PORTICO_PROCESS: `${API_URL}/portico/process`, // POST
  PORTICO_PROCESS_MULTIUSE: `${API_URL}/portico/multiuse`, // POST
  PORTICO_REFUND: `${API_URL}/portico/refund`, // POST

  // ---------------------------------------------------------------------------
  // Bambora
  // ---------------------------------------------------------------------------
  BAMBORA_PROCESS: `${API_URL}/bambora/process`, // POST
  BAMBORA_PROCESS_MULTIUSE: `${API_URL}/bambora/multiuse`, // POST
  BAMBORA_REFUND: `${API_URL}/bambora/refund`, // POST

  // ---------------------------------------------------------------------------
  // Payment Terminals
  // ---------------------------------------------------------------------------
  PAYMENT_TERMINAL_CREATE: `${API_URL}/paymentterminal`, // POST
  PAYMENT_TERMINAL_EDIT: (id) => `${API_URL}/paymentterminal/${id}`, // POST
  PAYMENT_TERMINALS_FOR_SITE: (id) =>
    `${API_URL}/site/${id}/paymentterminals`, // GET

  // ---------------------------------------------------------------------------
  // QuickBooks
  // ---------------------------------------------------------------------------
  QUICKBOOKS_AUTH_URL: (id) =>
    `${API_URL}/integrations/quickbooks/authurl/${id}`, // GET
  QUICKBOOKS_CLEAR_INVOICE: (id) =>
    `${API_URL}/integrations/quickbooks/post/invoice/${id}/clear`, // POST
  QUICKBOOKS_DEFAULT_CUSTOMER: (id) =>
    `${API_URL}/integrations/quickbooks/customers/${id}/default`, // POST
  QUICKBOOKS_FIND_CUSTOMERS: (id) =>
    `${API_URL}/integrations/quickbooks/customers/${id}`, // GET
  QUICKBOOKS_POST_INVOICE: (id) =>
    `${API_URL}/integrations/quickbooks/post/invoice/${id}`, // POST
  QUICKBOOKS_REVOKE: (id) =>
    `${API_URL}/integrations/quickbooks/revoke/${id}`, // POST
  QUICKBOOKS_STATUS: (id) =>
    `${API_URL}/integrations/quickbooks/status/${id}`, // GET
  QUICKBOOKS_STATUS_QUICK: (id) =>
    `${API_URL}/integrations/quickbooks/status/${id}/quick`, // GET
  QUICKBOOKS_TAXES: (id) =>
    `${API_URL}/integrations/quickbooks/taxes/${id}`, // GET

  // ---------------------------------------------------------------------------
  // Stripe Connect
  // ---------------------------------------------------------------------------
  STRIPE_AUTH_URL: (id) =>
    `${API_URL}/integrations/stripe/authurl/${id}`, // GET
  STRIPE_REFUND: `${API_URL}/integrations/stripe/refund`, // POST
  STRIPE_STATUS: (id) =>
    `${API_URL}/integrations/stripe/status/${id}`, // GET
  STRIPE_PAYMENT_INTENT: (id) =>
    `${API_URL}/integrations/stripe/paymentintent/${id}`,
  STRIPE_PAYMENT_INTENT_FROM_ORDER: (id, orderId) =>
    `${API_URL}/integrations/stripe/paymentintent/${id}/${orderId}`,

  // ---------------------------------------------------------------------------
  // Subscriptions
  // ---------------------------------------------------------------------------
  SUBSCRIPTIONS_CREATE:
    (siteId) => `${API_URL}/sites/${siteId}/subscriptions`, // POST
  SUBSCRIPTIONS_CSV_TOKEN: `${API_URL}/subscriptions/download/csv/token`, // GET
  SUBSCRIPTIONS_CSV_DOWNLOAD: (token) =>
    `${API_URL}/subscriptions/csv/${token}`,
  SUBSCRIPTIONS_EDIT: (siteId, id) =>
    `${API_URL}/sites/${siteId}/subscriptions/${id}`, // POST
  SUBSCRIPTIONS_EDIT_MULTI:
    (siteId) => `${API_URL}/sites/${siteId}/subscriptions`, // POST
  SUBSCRIPTIONS: `${API_URL}/subscriptions`, // GET
  SUBSCRIPTION: (id) => `${API_URL}/subscriptions/${id}`, // GET
  SUBSCRIPTION_DELETE: (siteId, id) =>
    `${API_URL}/sites/${siteId}/subscriptions/${id}/delete`, // POST
  SUBSCRIPTION_UNDELETE: (siteId, id) =>
    `${API_URL}/sites/${siteId}/subscriptions/${id}/undelete`, // POST
  SUBSCRIPTION_LOG: (id, lid) =>
    `${API_URL}/subscriptions/${id}/log/${lid}`, // POST
  SUBSCRIPTION_PAUSE: (siteId, id) =>
    `${API_URL}/sites/${siteId}/subscriptions/${id}/pause`, // POST
  SUBSCRIPTION_UNPAUSE: (siteId, id) =>
    `${API_URL}/sites/${siteId}/subscriptions/${id}/unpause`, // POST
  // We use the alternative URL prefix here to allow our load balancer to
  // run all subscriptions on a particular backend via prefix routing.
  SUBSCRIPTION_RUN: (siteId, id) =>
    `${API_URL}/sites/subscriptions/${siteId}/subscriptions/${id}/run`, // POST
  SUBSCRIPTION_TEST: (siteId, id) =>
    `${API_URL}/sites/subscriptions/${siteId}/subscriptions/${id}/test`, // POST

  // ---------------------------------------------------------------------------
  // Slack APIs
  // ---------------------------------------------------------------------------
  SLACK_AUTH_REVOKE_SITE: (siteId) =>
    `${API_URL}/integrations/slack/${siteId}/revoke`, // POST
  SLACK_AUTH_REVOKE_PORTAL: (portalId) =>
    `${API_URL}/integrations/slack/portal/${portalId}/revoke`, // POST
  SLACK_AUTH_OPTIONS_SITE: (siteId) =>
    `${API_URL}/integrations/slack/${siteId}`, // POST
  SLACK_AUTH_OPTIONS_PORTAL: (portalId) =>
    `${API_URL}/integrations/slack/portal/${portalId}`, // POST
  SLACK_AUTH_STATUS_SITE: (siteId) =>
    `${API_URL}/integrations/slack/status/${siteId}`, // GET
  SLACK_AUTH_STATUS_PORTAL: (portalId) =>
    `${API_URL}/integrations/slack/status/portal/${portalId}`, // GET
  SLACK_AUTH_URL_SITE: (siteId) =>
    `${API_URL}/integrations/slack/authurl/${siteId}`, // GET
  SLACK_AUTH_URL_PORTAL: (portalId) =>
    `${API_URL}/integrations/slack/authurl/portal/${portalId}`, // GET

  // ---------------------------------------------------------------------------
  // Google APIs
  // ---------------------------------------------------------------------------
  GOOGLE_API_CALENDARS: `${API_URL}/integrations/google/calendars`, // GET
  GOOGLE_AUTH_REVOKE: `${API_URL}/integrations/google/revoke`, // POST
  GOOGLE_AUTH_STATUS: `${API_URL}/integrations/google/status`, // GET
  GOOGLE_AUTH_URL: `${API_URL}/integrations/google/authurl`, // GET
  GOOGLE_INTEGRATION_SYNC_FEEDS:
    `${API_URL}/integrations/google/syncfeeds`, // GET
  GOOGLE_INTEGRATION_SYNC_FEED_CREATE:
    `${API_URL}/integrations/google/syncfeeds/create`, // POST
  GOOGLE_INTEGRATION_SYNC_FEED_DEACTIVATE:
    `${API_URL}/integrations/google/syncfeeds/deactivate`, // POST

  // ---------------------------------------------------------------------------
  // Microsoft APIs
  // ---------------------------------------------------------------------------
  MICROSOFT_API_CALENDARS: `${API_URL}/integrations/microsoft/calendars`, // GET
  MICROSOFT_AUTH_REVOKE: `${API_URL}/integrations/microsoft/revoke`, // POST
  MICROSOFT_AUTH_STATUS: `${API_URL}/integrations/microsoft/status`, // GET
  MICROSOFT_AUTH_URL: `${API_URL}/integrations/microsoft/authurl`, // GET
  MICROSOFT_INTEGRATION_SYNC_FEEDS:
    `${API_URL}/integrations/microsoft/syncfeeds`, // GET
  MICROSOFT_INTEGRATION_SYNC_FEED_CREATE:
    `${API_URL}/integrations/microsoft/syncfeeds/create`, // POST
  MICROSOFT_INTEGRATION_SYNC_FEED_DEACTIVATE:
    `${API_URL}/integrations/microsoft/syncfeeds/deactivate`, // POST

  // ---------------------------------------------------------------------------
  // General use
  // ---------------------------------------------------------------------------
  ACTUAL_USAGE: `${API_URL}/actualusage`,
  ACTUAL_USAGE_BY_ID: (id) => `${API_URL}/actualusage/${id}`,
  ACCOUNT_TRANSACT: `${API_URL}/account/transact`, // POST
  ACCOUNTS: `${API_URL}/accounts`,
  ACCOUNTS_CSV: `${API_URL}/accounts/csv`,
  ACCOUNTS_CSV_TRANSACTIONS: `${API_URL}/accounts/csvtransactions`,
  ACCOUNTS_FOR_TIMESPAN: `${API_URL}/account/accountsfortimespan`,
  ACCOUNT_BALANCE: (id) => `${API_URL}/account/${id}/balance`,
  ACCOUNTS_OVERVIEW: `${API_URL}/account/list`,
  ACCOUNTS_PORTAL_USER_CREATE_MODIFY: `${API_URL}/account/portaluser`,
  ACCOUNTS_SITE_USER_CREATE_MODIFY: `${API_URL}/account/siteuser`,
  ANNOUNCEMENTS_LOGIN: `${API_URL}/announcements/login`,
  ANNOUNCEMENTS_APP: `${API_URL}/announcements/app`,
  APPROVAL: (id) => `${API_URL}/approvals/${id}`,
  APPROVAL_FROM_TOKEN: (token) => `${API_URL}/approvals/token/${token}`,
  APPROVALS: `${API_URL}/approvals`,
  APPROVALS_UPDATE: (id) => `${API_URL}/approvals/${id}`,
  APPROVALS_HISTORY: ({ id, offset, limit, status, includeCancelled } = {}) =>
    `${API_URL}/approvals?approver_id=${id}` +
    `&limit=${limit}&offset=${offset}&reservation_approvals=true` +
    '&start=1&end=2147483647&return_count=true&order_by=created' +
    `&status=${status}&include_cancelled=${includeCancelled}`,
  APPROVERS: (id) => `${API_URL}/sites/${id}/approvers`,
  APPROVERS_UPDATE: (id) => `${API_URL}/sites/${id}/approvers`, // POST
  AUDIT_LOG: `${API_URL}/auditlog`, // GET
  BLACKOUT_ADD: (id) =>
    `${API_URL}/reservables/${id}/weeklyevent/block`, // POST
  BLACKOUTS_CLEAR: `${API_URL}/weeklyevent/blackouts/delete/bulk`, // POST
  BROWSE: `${API_URL}/browse`,
  CANCELLATION_NTFNS_ADD: (id) => `${API_URL}/users/subscribe?` +
    `reservable_cancellation_id=${id}`,
  CANCELLATION_NTFNS_REMOVE: (id) => `${API_URL}/users/unsubscribe?` +
    `reservable_cancellation_id=${id}`,
  CATEGORIES: `${API_URL}/reservables/categories`,
  CONTACT: (id) => `${API_URL}/users/${id}/sendmessage`,
  DEFAULT_PORTAL: `${API_URL}/users/self/default_portal`,
  EMBEDS_FOR_PORTAL: (portalId) =>
    `${API_URL}/embed?portal_id=${portalId}`, // GET
  EMBEDS_FOR_SITE: (siteId) =>
    `${API_URL}/embed?site_id=${siteId}`, // GET
  EMBED_BASEURL: `${API_URL}/embed/baseurl`, // GET
  EMBED_CREATE: `${API_URL}/embed`, // POST
  EMBED_EDIT: (embedId) => `${API_URL}/embed/${embedId}`, // POST
  EMBED_QRCODE: (embedId) => `${API_URL}/embed/${embedId}/qrcode`,
  EMBEDDED_SENSE_SENSOR: `${API_URL}/embeddedsense/sensor`, // POST
  EMBEDDED_SENSE_SITE_SENSORS: (siteId) =>
    `${API_URL}/${siteId}/embeddedsense/sensors`, // GET
  EMBEDDED_IMAGE_UPLOAD_FOR_PROJECT: (projectId) =>
    `${API_URL}/project/${projectId}/image/embedded`, // POST
  EMBEDDED_IMAGE_UPLOAD_FOR_PORTAL: (portalId) =>
    `${API_URL}/portals/${portalId}/image/embedded`, // POST
  EMBEDDED_IMAGE_UPLOAD_FOR_SITE: (siteId) =>
    `${API_URL}/sites/${siteId}/image/embedded`, // POST
  EXTERNAL_SUBMISSION_FORMS: (id) =>
    `${API_URL}/sites/${id}/external_submission_forms`,
  FAVORITES: (id) => `${API_URL}/users/${id}/favs`,
  FAVORITE_ADD: (id) => `${API_URL}/reservables/${id}/fav`, // POST
  FAVORITE_REMOVE: (id) => `${API_URL}/reservables/${id}/fav/remove`, // POST
  FEDERATION_MEMBERSHIP_SET_EMAIL: `${SAML_URL}/email`, // POST
  FILE_URL: (id) => `${API_URL}/f/${id}/url`, // GET
  FILE_DOWNLOAD_FORM_FILE: `${API_URL}/reservation/form/file`, // GET
  FILE_DELETE_FS_FILE: (id) => `${API_URL}/f/${id}/delete`,
  FILE_MODIFY: (id) => `${API_URL}/f/${id}/modify`,
  FORM: (id) => `${API_URL}/forms/${id}`, // GET
  FORM_CLONE: (id) => `${API_URL}/forms/${id}/clone`, // POST
  FORM_CREATE: `${API_URL}/forms`, // POST
  FORM_DELETE: (id) => `${API_URL}/forms/${id}/delete`, // POST
  FORM_REACTIVATE: (id) => `${API_URL}/forms/${id}/reactivate`, // POST
  FORM_RESPONSE_FILE: (formResponseId, fieldId, file, token) =>
    `${API_URL}/form_response/${formResponseId}/fields/`
    + `${fieldId}/files?fname=${file}&qauthtoken=${token}`,
  FORM_TRANSFER: (id) => `${API_URL}/forms/${id}/transfer`, // POST
  FORM_UPDATE: (id) => `${API_URL}/forms/${id}`, // POST
  KEYVALUE_GET: (k) => `${API_URL}/usersetting/get/${k}`,
  INVOICE: (id) => `${API_URL}/invoices/${id}`, // GET
  INVOICE_ACCEPT: (id) => `${API_URL}/invoices/${id}/accept`,
  INVOICE_ADD: (siteId) => `${API_URL}/sites/${siteId}/invoices`, // POST
  INVOICE_EMAIL_PREVIEW: (id) =>
    `${API_URL}/invoices/${id}/email_preview.html`, // POST
  INVOICE_EVENTS: (siteId, id) =>
    `${API_URL}/auditlog?invoice_id=${id}&site_id=${siteId}`, // GET
  INVOICE_FILE_UPLOAD: (id) => `${API_URL}/upload/invoice/${id}/file`, // POST
  INVOICE_FILES: (id) => `${API_URL}/invoices/${id}/files`, // GET
  INVOICE_FROM_EXTERNAL: (id) => `${API_URL}/invoices/external/${id}`, // GET
  INVOICE_SEND_EMAIL: (id) =>
    `${API_URL}/invoices/${id}/send_email`, // POST
  INVOICES: `${API_URL}/invoices`, // GET
  INVOICES_CSV_TOKEN: `${API_URL}/invoices/csv/token`, // GET
  INVOICE_DELETE_PAYMENT: (id, paymentId) =>
    `${API_URL}/invoices/${id}/delete_payment/${paymentId}`,
  INVOICE_RECORD_PAYMENT: (id) => `${API_URL}/invoices/${id}/record_payment`,
  INVOICE_TAX_RATES: (siteId) => `${API_URL}/sites/${siteId}/taxrates`,
  KEYVALUE_SET: `${API_URL}/usersetting`, // POST
  KIOSK_CLONE: (id) => `${API_URL}/kiosk/${id}/clone`,
  KIOSK_IMAGE_ADD: (id) => `${API_URL}/kiosk/${id}/image`,
  KIOSK_IMAGE_DELETE: (id) => `${API_URL}/kiosk/${id}/image/delete`,
  KIOSK_QRCODE: (id) => `${API_URL}/kiosk/${id}/qrcode`,
  KIOSKS: (id) => `${API_URL}/sites/${id}/kiosks`,
  LOG_HIT: `${API_URL}/log/hit`,

  MAINTENANCE: (id) => `${API_URL}/maintenance?maintenance_id=${id}`,
  MAINTENANCE_DELETE: (rId, mId) =>
    `${API_URL}/reservables/${rId}/maintenance/${mId}/cancel`,

  MAINTENANCE_FILE: (id, file, token) =>
    `${API_URL}/maintenance/${id}/files?fname=${file}&qauthtoken=${token}`,
  MAINTENANCE_FILE_UPLOAD: (id) =>
    `${API_URL}/upload/history/${id}/file`, // POST

  // MAINTENANCE_FOR_RESERVABLE returns all scheduled maintenance for a
  // reservable within the time range start ... end. The start and end
  // times should be in unix uint32 timestamps.
  MAINTENANCE_FOR_RESERVABLE: (id, start, end) =>
    `${API_URL}/maintenance?&start=${start}&end=${end}&reservable_id=${id}`,

  // MAINTENANCE_FOR_RESERVABLE_ALL returns all scheduled maintenance for a
  // reservable within the time range start ... end. The start and end
  // times should be in unix uint32 timestamps.
  MAINTENANCE_FOR_RESERVABLE_ALL: (id, start, end) =>
    `${API_URL}/maintenance?&start=${start}&end=${end}&reservable_id=${id}` +
    '&include_nonblockers=t',

  // MAINTENANCE_FOR_SITE returns all scheduled maintenance for a given
  // site's reservables within the time range start ... end. The start
  // and end times should be in unix uint32 timestamps.
  MAINTENANCE_FOR_SITE: (id, start, end) =>
    `${API_URL}/maintenance?&start=${start}&end=${end}&site_id=${id}`,

  // MAINTENANCE_FOR_SITE_ALL returns all scheduled maintenance for a
  // reservable within the time range start ... end. The start and end
  // times should be in unix uint32 timestamps.
  MAINTENANCE_FOR_SITE_ALL: (id, start, end) =>
    `${API_URL}/maintenance?&start=${start}&end=${end}&site_id=${id}` +
    '&include_nonblockers=t',

  MAINTENANCE_RESERVE: (id) => `${API_URL}/reservables/${id}/maintenance`,

  // MAINTENANCES returns the naked API route for maintenance.
  MAINTENANCES: `${API_URL}/maintenance`,

  MEMBERSHIPS: `${API_URL}/sites?superslim=true`,
  MEMBERSHIPS_RAW: `${API_URL}/sites`,
  MEMBERSHIP_DELETE: (siteId, userId) =>
    `${API_URL}/sites/${siteId}/users/${userId}/delete`, // POST
  MEMBERSHIP_UPDATE: (id) => `${API_URL}/sites/${id}/requestaccess`, // POST
  PAYMENTS: `${API_URL}/invoices/payments`, // GET
  PAYMENTS_CSV_TOKEN: `${API_URL}/invoices/payments/csv/token`, // GET
  PING: `${API_URL}/ping/login`,
  PLANS: `${API_URL}/plans`,
  PLAN: (code) => `${API_URL}/plans/${code}`,
  PORTAL: (id) => `${API_URL}/portals/${id}`,
  PORTAL_BULK_ADMIN_MESSAGE: (id) =>
    `${API_URL}/portals/${id}/bulkadminmessage`, // POST
  PORTAL_FORMS: (id) => `${API_URL}/portals/${id}/forms`,
  PORTAL_FORMS_DEACTIVATED: (id) =>
    `${API_URL}/portals/${id}/forms?deactivated=true`,
  PORTAL_FRESHNESS: (id) => `${API_URL}/portals/${id}/freshness`, // GET
  PORTAL_FRESHNESS_CHART: (id) =>
    `${API_URL}/portals/${id}/freshness?chart=true`, // GET
  PORTAL_IMAGE_ADD: (id) => `${API_URL}/portals/${id}/image`, // POST
  PORTAL_IMAGE_DELETE: (id) => `${API_URL}/portals/${id}/image/delete`, // POST
  PORTAL_REPORT_ACTIVE_USERS: (id) =>
    `${API_URL}/portals/${id}/reports/activeusers`, // GET
  PORTAL_REPORT_MESSAGING: (id) =>
    `${API_URL}/reports/messaging?type=portal&key=${id}`, // GET
  PORTAL_REPORT_MESSAGING_RAW: `${API_URL}/reports/messaging`, // GET
  PORTAL_REPORT_RESERVATIONS: (id) =>
    `${API_URL}/portals/${id}/reports/reservations`, // GET
  PORTAL_REPORT_RESOURCES: (id) =>
    `${API_URL}/portals/${id}/reports/resources`, // GET
  PORTAL_REPORT_RESOURCES_TAGS: (id) =>
    `${API_URL}/portals/${id}/reports/resources/tags`, // GET
  PORTAL_REPORT_SEARCHES: (id) =>
    `${API_URL}/portals/${id}/reports/searches`, // GET
  PORTAL_REPORT_SITE_USERS: (id) =>
    `${API_URL}/portals/${id}/reports/siteusers`, // GET
  PORTAL_REPORT_SITES: (id) => `${API_URL}/portals/${id}/reports/sites`, // GET
  PORTAL_RESERVABLE_PROPERTY_DEFINITIONS: (id) =>
    `${API_URL}/portals/${id}/reservablepropdefs`, // GET
  PORTAL_RESERVATION_FORMS: (id) => `${API_URL}/portals/${id}/reservationforms`,
  PORTAL_SITE: (id, sId) =>
    `${API_URL}/portals/${id}/site/${sId}`, // POST
  PORTAL_SITE_PROPERTY_DEFINITIONS: (id) =>
    `${API_URL}/portals/${id}/sitepropdefs`, // GET
  PORTAL_SITE_REMOVE: (id, sId) =>
    `${API_URL}/portals/${id}/site/${sId}/remove`, // POST
  PORTAL_SITE_USER_PROPERTY_DEFINITIONS: (id) =>
    `${API_URL}/portals/${id}/siteuserpropdefs`, // GET
  PORTAL_SITE_RESERVABLE_FIELDS: (id) => `${API_URL}/portalsitefields/${id}`,
  PORTAL_SITES: (id, limit, offset, q) => {
    if (q) {
      return `${API_URL}/portals/${id}/sites`
        + `?limit=${limit}&offset=${offset}&q=${q}`;
    }
    return `${API_URL}/portals/${id}/sites`
      + `?limit=${limit}&offset=${offset}`; // GET
  },
  PORTAL_TAGS: (portalId) =>
    `${API_URL}/reservables/tags?portal_id=${portalId}`, // GET
  PORTAL_UPDATE: (id) => `${API_URL}/portals/${id}`, // POST
  PORTAL_USER: (id, uId) => `${API_URL}/portals/${id}/users/${uId}`,
  PORTAL_USER_ADD: (id) => `${API_URL}/portals/${id}/adduser`,
  PORTAL_USER_DELETE: (id, uId) =>
    `${API_URL}/portals/${id}/users/${uId}/remove`,
  PORTAL_USER_MODIFY: (id, uId) => `${API_URL}/portals/${id}/users/${uId}`,
  PORTAL_USERS: (id) => `${API_URL}/portals/${id}/users`,
  PORTAL_USER_AUDIT_SEARCH: (id) => `${API_URL}/portals/${id}/useraudit/search`,
  PORTAL_USER_API_KEY_CLEAR: (pid, uid) =>
    `${API_URL}/portals/${pid}/users/${uid}/apikey/clear`, // POST
  PORTAL_USER_API_KEY_CREATE: (pid, uid) =>
    `${API_URL}/portals/${pid}/users/${uid}/apikey/create`, // POST
  PORTAL_USER_API_KEY_MODIFY: (pid, uid) =>
    `${API_URL}/portals/${pid}/users/${uid}/apikey/modify`, // POST
  PORTALS: `${API_URL}/portals`,
  PROJECT: (id) => `${API_URL}/project/${id}`, // GET
  PROJECT_ADD: (id) => `${API_URL}/projects/${id}/create`, // POST
  PROJECT_EMAIL_DIGESTS: (id) => `${API_URL}/projects/${id}/emails`, // POST
  PROJECT_EVENTS: (id) => `${API_URL}/project/${id}/events`, // GET
  PROJECT_FILE_UPLOAD: (id) => `${API_URL}/upload/project/${id}/file`, // POST
  PROJECT_FILES: (id) => `${API_URL}/project/${id}/files`, // GET
  PROJECT_MODIFY: `${API_URL}/projects/modify`, // POST
  PROJECT_NOTES: (id) => `${API_URL}/project/${id}/notes`, // GET
  PROJECT_NOTES_CREATE_MODIFY: (id) => `${API_URL}/projects/${id}/note`, // POST
  PROJECT_OVERVIEW: (id) => `${API_URL}/project/${id}/overview`, // GET
  PROJECT_PIN_RESERVATION: (id, resId) =>
    `${API_URL}/projects/${id}/reservation/${resId}/pin`, // POST
  PROJECT_SETUSERS: `${API_URL}/projects/setusers`, // POST
  PROJECT_UNPIN_RESERVATION: (id, resId) =>
    `${API_URL}/projects/${id}/reservation/${resId}/unpin`, // POST
  PROJECT_USERGROUPS: (id) => `${API_URL}/project/${id}/usergroups`, // GET
  PROJECT_USERS: (id) => `${API_URL}/project/${id}/users`, // GET
  PROJECT_USERS_ADD_MODIFY: `${API_URL}/projects/setusers`, // POST
  PROJECT_TAGS: `${API_URL}/projects/tags`, // POST
  PROJECT_TAGS_FOR_SITE: (id) =>
    `${API_URL}/projects/tags?site_id=${id}`, // GET
  PROJECT_TAGS_FOR_USER: (id) =>
    `${API_URL}/projects/tags?user_id=${id}`, // GET
  PROJECT_TAGS_FOR_PORTAL: (id) =>
    `${API_URL}/projects/tags?portal_id=${id}`, // GET
  PROJECT_TAG_LINK: (id) => `${API_URL}/projects/${id}/tag/link`, // POST
  PROJECT_TAG_UNLINK: (id) => `${API_URL}/projects/${id}/tag/unlink`, // POST
  PROJECTS: `${API_URL}/projects`, // GET
  PROJECTS_FOR_SITE: (id) => `${API_URL}/projects/${id}`, // GET
  PROJECTS_FROM_RESERVATIONS: `${API_URL}/user/projects/reservation`, // GET
  PROPDEFS: `${API_URL}/propdefs`,
  PROPDEFS_DELETE: `${API_URL}/propdefs/delete`, // POST
  QUICK_APPROVE: `${API_URL}/quickapprove`, // POST

  RATE_BLACKOUT_ADD: (id) => `${API_URL}/rates/${id}/blackouts`, // POST
  RATE_BLACKOUT_DELETE: (id) => `${API_URL}/rateblackouts/${id}/delete`, // POST

  RELEASE_NOTES_LATEST: `${API_URL}/releasenotes/latest`,
  RELEASE_NOTES: `${API_URL}/releasenotes`,
  REPORT_ERROR: `${API_URL}/error_report`, // POST
  REPORTS_CUSTOM_AVAILABLE_PORTAL: (portalid) =>
    `${API_URL}/reports/custom/list/portal/${portalid}`, // GET
  REPORTS_CUSTOM_AVAILABLE_SITE: (siteid) =>
    `${API_URL}/reports/custom/list/site/${siteid}`, // GET
  REPORT_CUSTOM_TOKEN: `${API_URL}/reports/custom/token`, // POST
  REPORT_CUSTOM: (token) =>
    `${API_URL}/reports/custom/${token}`, // GET
  REPORT_CSV_TOKEN_DOWNLOAD: (token) => `${API_URL}/reports/csv/${token}`,

  // REQUESTS_FOR_RESERVABLE returns on request reservations for
  // some reservable.
  // The start and end times should be in unix uint32 timestamps.
  REQUESTS_FOR_RESERVABLE: (rId, userId, start, end) =>
    `${API_URL}/reservationsuser?start=${start}&end=${end}` +
    `&reservable_id=${rId}&requests_only=true&include_events=true` +
    `&user_id=${userId}&include_form_responses=true`,

  // REQUESTS_FOR_SITE returns all reservations for some site.
  // The start and end times should be in unix uint32 timestamps.
  REQUESTS_FOR_SITE: (id, start, end) =>
    `${API_URL}/reservations?&start=${start}&end=${end}&site_id=${id}` +
    '&requests=true',

  RESERVE: (id) => `${API_URL}/reservables/${id}/reserve`,
  RESERVE_BASE: `${API_URL}/reservation`,
  RESERVABLE: (id) => `${API_URL}/reservables/${id}`,
  RESERVABLE_ATTACHED_FORMS: (id) =>
    `${API_URL}/reservables/${id}/reservationforms`,
  RESERVABLE_ATTACHED_FORMS_BULK: `${API_URL}/reservables/reservationforms`,
  RESERVABLE_CLASSES_FOR_PORTAL: (pId) =>
    `${API_URL}/reservables/classes/portal/${pId}`,
  RESERVABLE_CLASSES_FOR_SITE: (sId) =>
    `${API_URL}/reservables/classes/site/${sId}`,
  RESERVABLE_CLONE: (id) => `${API_URL}/reservables/${id}/clone`,
  RESERVABLE_CLONE_SITE: (id, siteId) =>
    `${API_URL}/reservables/${id}/clone/${siteId}`,
  RESERVABLE_CONTACT: (id) => `${API_URL}/reservables/${id}/public_contact`,
  RESERVABLE_DELETE_IMAGE: (id) =>
    `${API_URL}/reservables/${id}/image/delete`, // POST
  RESERVABLE_EVENT_REGISTRATIONS: (id) =>
    `${API_URL}/reservables/${id}/event_registrations`,
  RESERVABLE_FILE_UPLOAD: (id) => `${API_URL}/upload/reservables/${id}/file`,
  RESERVABLE_FILES: (id) => `${API_URL}/reservables/${id}/files`,
  RESERVABLE_NAMED_UNITS: (id) => `${API_URL}/reservables/${id}/namedunits`,
  RESERVABLE_RATES_AVAILABLE: `${API_URL}/reservables/rates_available`,
  RESERVABLE_ROTATE_IMAGE: (id) =>
    `${API_URL}/reservables/${id}/image/rotate`, // POST
  RESERVABLE_SET_SCRIPTS: `${API_URL}/reservables/scripts`, // POST
  RESERVABLE_SUBSCRIBE_CANCEL_NOTIFICATIONS: (id) =>
    `${API_URL}/users/subscribe?reservable_cancellation_id=${id}`, // POST
  RESERVABLE_TAG: (id) => `${API_URL}/reservables/${id}/tag`,
  RESERVABLE_TAG_MASS: `${API_URL}/reservables/tags/tag`,
  RESERVABLE_UNSUBSCRIBE_CANCEL_NOTIFICATIONS: (id) =>
    `${API_URL}/users/unsubscribe?reservable_cancellation_id=${id}`, // POST
  RESERVABLE_UNTAG: (id) => `${API_URL}/reservables/${id}/untag`,
  RESERVABLE_UNTAG_MASS: `${API_URL}/reservables/tags/untag`,
  RESERVABLE_UPDATE: (id) => `${API_URL}/reservables/${id}/modify`, // POST
  RESERVABLE_UPDATE_NO_ID: `${API_URL}/reservables/modify`, // POST
  RESERVABLE_UPDATE_IMAGE: (id) => `${API_URL}/reservables/${id}/image`, // POST
  RESERVABLE_WITH_PORTAL: (id, pId) => `${API_URL}/reservables/${id}/p/${pId}`,
  RESERVABLES: `${API_URL}/reservables`,
  RESERVABLES_AVAILABLE: `${API_URL}/reservation/availability`, // POST
  RESERVABLES_BLOCKED: `${API_URL}/reservables/block`, // POST
  RESERVABLES_CREATE: `${API_URL}/reservables`, // POST
  RESERVABLES_CSV_DOWNLOAD: (token) => `${API_URL}/reservables/csv/${token}`,
  RESERVABLES_CSV_TOKEN: `${API_URL}/reservables.csv`, // GET
  RESERVABLES_FAST: `${API_URL}/reservables/fast`,
  RESERVABLES_FOR_PORTAL: (id, v) => {
    if (v) {
      return `${API_URL}/reservables?portal_id=${id}&verbose=true`;
    }
    return `${API_URL}/reservables?portal_id=${id}`;
  },
  RESERVABLES_FOR_SITE: (id, v) => {
    if (v) {
      return `${API_URL}/reservables?site_id=${id}&verbose=true`;
    }
    return `${API_URL}/reservables?site_id=${id}`;
  },
  RESERVABLES_GENERATE_BULK: (id) =>
    `${API_URL}/upload/reservables/generatebulk/site/${id}`, // POST
  RESERVABLES_GENERATE_BULK_EXAMPLE:
    `${API_URL}/reservables/generatebulk/template`, // GET
  RESERVABLES_GENERATE_BULK_EXAMPLE_FOR_SITE:
    (id, token) => `${API_URL}/reservables/generatebulk/template/site/${id}` +
    `?qauthtoken=${token}&t=${(new Date()).getTime()}`, // GET
  RESERVABLES_PUBLIC_FOR_SITE: (id) =>
    `${API_URL}/reservables_public?site_id=${id}&verbose=false`,
  RESERVABLES_RELATED: `${API_URL}/reservables/related`, // POST
  RESERVATION: (id) => `${API_URL}/reservations/${id}`,
  RESERVATION_CANCEL: (id) => `${API_URL}/reservation/${id}/cancel`,
  RESERVATION_CHECKIN_STATUS: (id, token) =>
    `${API_URL}/reservations/${id}/checkinstatus/${token}`,
  RESERVATION_CHECKIN: (id) =>
    `${API_URL}/reservations/${id}/checkin`,
  RESERVATION_CHECKIN_WITH_TOKEN: (id, token) =>
    `${API_URL}/reservations/${id}/checkin/${token}`,
  RESERVATION_CHECKOUT: (id) =>
    `${API_URL}/reservations/${id}/checkout`,
  RESERVATION_CHECKOUT_WITH_TOKEN: (id, token) =>
    `${API_URL}/reservations/${id}/checkout/${token}`,
  RESERVATION_END_EARLY: (id) => `${API_URL}/reservation/${id}/endearly`, // P
  RESERVATION_END_EARLY_PUBLIC: (token) =>
    `${API_URL}/reservation/endearly/${token}`, // POST
  RESERVATION_LATEST: (id) => `${API_URL}/reservations/${id}?latest=true`,
  RESERVATION_RECUR_CANCEL: (resId, id) =>
    `${API_URL}/recurringreservation/${resId}/${id}/cancel`,
  RESERVATION_RECUR_CANCEL_FUTURE: (resId, id) =>
    `${API_URL}/recurringreservation/${resId}/${id}/cancel/future`, // POST
  RESERVATION_SUBSCRIBE_INDIVIDUAL_NTFNS: (id) =>
    `${API_URL}/reservation/${id}/send_cancellation_notifications`, // POST
  RESERVATION_UPDATE: (id) => `${API_URL}/reservation/${id}`, // POST

  RESERVATIONS: `${API_URL}/reservations`,

  // RESERVATIONS_FOR_RESERVABLE returns reservations for some reservable.
  // The start and end times should be in unix uint32 timestamps.
  RESERVATIONS_FOR_RESERVABLE: (id, start, end) =>
    `${API_URL}/reservations?start=${start}&end=${end}&reservable_id=${id}` +
    '&include_blocked=true',

  // RESERVATIONS_FOR_RESERVABLE_ON_LOAN returns on loan reservations for
  // some reservable.
  // The start and end times should be in unix uint32 timestamps.
  RESERVATIONS_FOR_RESERVABLE_ON_LOAN: (id, start, end) =>
    `${API_URL}/reservations?start=${start}&end=${end}&reservable_id=${id}` +
    '&is_loan=true&statuses[]=approved&statuses[]=pending' +
    '&include_blackouts=false',

  RESERVATIONS_FOR_RESERVABLE_NAMED_UNIT_ON_LOAN: (id, nuID, start, end) =>
    `${API_URL}/reservations?start=${start}&end=${end}&reservable_id=${id}` +
    `&named_unit_id=${nuID}` +
    '&is_loan=true&statuses[]=approved&statuses[]=pending' +
    '&include_blackouts=false',

  // RESERVATIONS_FOR_USER_ON_LOAN returns on loan reservations for
  // some user.
  // The start and end times should be in unix uint32 timestamps.
  RESERVATIONS_FOR_USER_ON_LOAN: (id, siteId, start, end) =>
    `${API_URL}/reservations?start=${start}&end=${end}&user_id=${id}` +
    `&site_id=${siteId}` +
    '&is_loan=true&statuses[]=approved&statuses[]=pending' +
    '&include_blackouts=false',

  // RESERVATIONS_FOR_RESERVABLE_AND_USER returns reservations for some
  // reservable and user. The start and end times should be in unix uint32
  // timestamps.
  RESERVATIONS_FOR_RESERVABLE_AND_USER: (resId, usrId, start, end) =>
    `${API_URL}/reservations?start=${start}&end=${end}` +
    `&reservable_id=${resId}&user_id=${usrId}`,

  // RESERVATIONS_FOR_SITE returns all reservations for some site.
  // The start and end times should be in unix uint32 timestamps.
  RESERVATIONS_FOR_SITE: (id, start, end) =>
    `${API_URL}/reservations?&start=${start}&end=${end}&site_id=${id}` +
    '&include_blocked=true&include_blackouts=true&active_only=true',

  RESERVATIONS_HISTORY: (id, idx, l) => `${API_URL}/reservations?limit=${l}` +
    `&offset=${idx}&user_id=${id}&include_cancelled=true` +
    '&active_only=false&return_count=true&compress_multi=true',

  // RESERVATIONS_RATE_QUOTES returns all reservations for some site that
  // presently need rates to be set before the reservation can proceed.
  // The start and end times should be in unix uint32 timestamps.
  RESERVATIONS_RATE_QUOTES: (start, end) =>
    `${API_URL}/reservationssite?&start=${start}&end=${end}` +
    '&rate_requests_only=true',

  RESERVATIONS_SLIM: `${API_URL}/reservationssite`,
  RESERVATIONS_SLIM_REPORTS: `${API_URL}/reports/reservationssite`,

  RESET_PASSWORD: `${API_URL}/resetpassword`,
  RESOURCE_MAP_NEW: `${API_URL}/resourcemap`,
  RESOURCE_MAP: (id) => `${API_URL}/resourcemap/${id}`,
  RESOURCE_MAP_IMAGE_ADD: (id) => `${API_URL}/resourcemap/${id}/image`,
  RESOURCE_MAPS: `${API_URL}/resourcemap`,
  RESOURCE_MAP_STATUSES: (id) => `${API_URL}/resourcemap/${id}/statuses`,
  RESOURCE_MAP_STATUSES_WITH_TIMES: (id, start, end) =>
    `${API_URL}/resourcemap/${id}/statuses?start=${start}&end=${end}`,
  RESOURCE_MAP_STATUSES_WITH_TIMES_AND_USER_IDS: (id, start, end) =>
    `${API_URL}/resourcemap/${id}/statuses?start=${start}&end=${end}` +
    '&include_user_ids=true',
  RSVP: `${API_URL}/reservation/rsvp`, // POST
  SCRIPT_EVALUATE: `${API_URL}/scripts`, // POST
  SEARCH: `${API_URL}/search/public`,
  SEARCH_SITES: (name, limit) => `${API_URL}/search/site?q=${name}` +
    `&limit=${limit}`,
  SELF: `${API_URL}/users/self`,
  SITE: (id) => `${API_URL}/sites/${id}`,
  SITE_FOR_SETTINGS: (id) => `${API_URL}/sites/${id}?full=t`,
  SITE_ACCESS_URLS: (id) => `${API_URL}/sites/${id}/accessurls`,
  SITE_ATTACHED_RESERVATION_FORMS_ALL: (id) =>
    `${API_URL}/sites/${id}/reservationforms/all`,
  SITE_BILLING_REPORT: (id) =>
    `${API_URL}/billing/reports/sites/${id}/all`,
  SITE_BILLING_REPORT_CSV_TOKEN: (id) =>
    `${API_URL}/billing/reports/sites/${id}/all/csv`,
  SITE_BILLING_REPORT_CSV_DETAILS_TOKEN: (id) =>
    `${API_URL}/billing/reports/sites/${id}/all/csvdetails`,
  SITE_BILLING_REPORT_CSV_CONDENSED_TOKEN: (id) =>
    `${API_URL}/sites/${id}/billing/reports/all/csvcondensed`,
  SITE_BILLING_REPORT_DOWNLOAD: (token) => `${API_URL}/reports/csv/${token}`,
  SITE_BILLING_REPORTS: (id) =>
    `${API_URL}/sites/${id}/billing/reports`,
  SITE_CACHE_NONCE_RESERVABLES: (id) =>
    `${API_URL}/sites/${id}/cachenonce/reservables`,
  SITE_CAN_EDIT: (id) => `${API_URL}/sites/${id}?can_edit=t`,
  SITE_CAPACITY_REPORT: (id, start, end) =>
    `${API_URL}/reports/capacity?start=${start}&end=${end}&site=${id}`,
  SITE_CAPACITY_REPORT_CSV: (id, start, end) =>
    `${API_URL}/reports/capacity/csv?start=${start}&end=${end}&site=${id}`,
  SITE_CHILDREN: (id) => `${API_URL}/sites/${id}/children`, // POST
  SITE_CONTACT: (id) => `${API_URL}/sites/${id}/public_contact`,
  SITE_CREATE: `${API_URL}/sites`,
  SITE_DEACTIVATE: (id) => `${API_URL}/sites/${id}/deactivate`, // POST
  SITE_EMAILS_IN_SITE: (id) => `${API_URL}/sites/${id}/emails_in_site`, // POST
  SITE_FORMS: (id) => `${API_URL}/sites/${id}/forms`, // GET
  SITE_FORMS_INCLUDE_PORTAL_FORMS: (id) =>
    `${API_URL}/sites/${id}/forms?include_portal_forms=true`, // GET
  SITE_FORMS_DEACTIVATED: (id) =>
    `${API_URL}/sites/${id}/forms?deactivated=true`,
  SITE_HAS_ANY_AUTOCANCEL: (id) =>
    `${API_URL}/sites/${id}/reservables/has_any_autocancel_window`, // GET
  SITE_HEATMAP: (id, start, end) =>
    `${API_URL}/reports/advanced?site=${id}&start=${start}&end=${end}`
    + '&by_reservable=true&by_user=true&by_user_group=true',
  SITE_ID_FROM_RESERVABLE_ID: (id) => `${API_URL}/reservables/${id}/site`,
  SITE_IMAGE_ADD: (id) => `${API_URL}/sites/${id}/image`, // POST
  SITE_IMAGE_DELETE: (id) => `${API_URL}/sites/${id}/image/delete`, // POST
  SITE_INTEGRATIONS: (id) => `${API_URL}/sites/${id}/integrations`,
  SITE_LINKS_CREATE_MODIFY: (id) => `${API_URL}/sites/${id}/links/addedit`, // P
  SITE_LINKS_MOVE: (id) => `${API_URL}/sites/${id}/links/move`, // P
  SITE_LINKS_SITE_WIDE: (id) => `${API_URL}/sites/${id}/links`, // GET
  SITE_NAMED_UNITS: (id) => `${API_URL}/sites/${id}/namedunits`,
  SITE_PLAN_HISTORY: (id) => `${API_URL}/sites/${id}/plan/history`, // GET
  SITE_PLAN_PREMIUM_TRIAL: (id) => `${API_URL}/sites/${id}/plan/premiumtrial`,
  SITE_PORTALS: (id) => `${API_URL}/sites/${id}/portals`, // GET
  SITE_PROPERTY_DEFINITIONS: (id) =>
    `${API_URL}/sites/${id}/sitepropdefs`, // GET
  SITE_RESERVABLE_PROPERTY_DEFINITIONS: (id) =>
    `${API_URL}/sites/${id}/reservablepropdefs`, // GET
  SITE_RESERVABLE_PROPERTY_DEFINITIONS_FILTERABLE: (id) =>
    `${API_URL}/sites/${id}/reservablepropdefs/filterable`, // GET
  SITE_RESERVABLE_PROPERTIES_VISIBILITY: (id) =>
    `${API_URL}/sites/${id}/properties/visibility`, // POST
  SITE_RESERVABLE_FIELDS: (id) => `${API_URL}/sites/${id}/reservables_fields`,
  SITE_RESERVABLES_QRCODE_SHEET: (siteId) =>
    `${API_URL}/sites/${siteId}/resources/qrcodesheet.pdf`,
  SITE_RESERVABLE_RATES: (id) => `${API_URL}/rates/site/${id}`,
  SITE_RESERVABLE_RATES_CSV: (id) => `${API_URL}/${id}/reservables/rates/csv`,
  SITE_RESERVABLE_RATES_CSV_DOWNLOAD: (token) =>
    `${API_URL}/reservables/rates/csv/${token}`, // Get
  SITE_RESERVABLE_RATES_CSV_TEMPLATE:
    `${API_URL}/reservables/rates/generatebulk/template`,
  SITE_RESERVABLE_RATES_GENERATE_BULK: (id) =>
    `${API_URL}/upload/rates/generatebulk/site/${id}`,
  SITE_RESERVABLE_RATES_UNIQUE: (id) => `${API_URL}/rates/site/${id}/unique`,
  SITE_RESERVATION_CSV_TEMPLATE: `${API_URL}/reservation/generatebulk/template`,
  SITE_RESERVATION_FORMS: (id) => `${API_URL}/sites/${id}/reservationforms`,
  SITE_RESERVATION_SCRIPT: (id) =>
    `${API_URL}/sites/${id}/reservations/scripts/addedit`, // POST
  SITE_RESERVATION_SCRIPT_FETCH: (siteId, scriptId) =>
    `${API_URL}/sites/${siteId}/reservations/scripts/${scriptId}`, // GET
  SITE_SCRIPTS: (id) => `${API_URL}/sites/${id}/reservations/scripts`, // GET
  SITE_TRANSFER_OWNER: (id) => `${API_URL}/sites/${id}/owner`,
  SITE_UPDATE: (id) => `${API_URL}/sites/${id}`,
  SITE_USER: (sid, uid) => `${API_URL}/sites/${sid}/users/${uid}`,
  SITE_USER_ACCOUNT_NUMBERS: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/account_numbers`, // POST
  SITE_USER_ACTUAL_USAGE_IDENTIFIERS: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/actual_usage_identifiers`, // POST
  SITE_USER_ADD: (id) => `${API_URL}/sites/${id}/adduser`,
  SITE_USER_API_KEY_CLEAR: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/apikey/clear`, // POST
  SITE_USER_API_KEY_CREATE: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/apikey/create`, // POST
  SITE_USER_API_KEY_MODIFY: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/apikey/modify`, // POST
  SITE_USER_API_KEY_LOGS: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/apikey/logs`, // GET
  SITE_USER_API_KEY_LOGS_CSV: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/apikey/logs/csv`, // GET
  SITE_USER_BILLING_ADDRESS: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/billing_address`, // POST
  SITE_USER_CLONE: (sid, uid) => `${API_URL}/sites/${sid}/users/${uid}/clone`,
  SITE_USER_DELETE: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/delete`, // POST
  SITE_USER_FILES: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/files`,
  SITE_USER_FILE_UPLOAD: (sid, uid) =>
    `${API_URL}/upload/sites/${sid}/users/${uid}/file`, // POST
  SITE_USER_INVOICE_PROPS: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/invoiceprops`,
  SITE_USER_MODIFY: (sid, uid) => `${API_URL}/sites/${sid}/users/${uid}`, // P
  SITE_USER_MODIFY_BULK: (sid, uid) => `${API_URL}/sites/${sid}/users`, // P
  SITE_USER_MULTIUSE_CC_TOKEN: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/multiusecctoken`, // GET
  SITE_USER_MULTIUSE_CC_TOKEN_CLEAR: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/multiusecctoken/clear`, // POST
  SITE_USER_MULTIUSE_CC_TOKEN_VERIFY: (token) =>
    `${API_URL}/multiusecctoken/${token}/verify`, // GET
  SITE_USER_PROPERTY_DEFINITIONS: (id) =>
    `${API_URL}/sites/${id}/siteuserpropdefs`, // GET
  SITE_USER_PROPERTIES: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/properties`, // POST
  SITE_USER_QRID_CODE: (siteId, id, token) =>
    `${API_URL}/sites/${siteId}/qridqrcode/${id}?` +
    `qauthtoken=${token}&t=${(new Date()).getTime()}`, // GET image
  SITE_USER_QRID_SHEET: (siteId) =>
    `${API_URL}/sites/${siteId}/qridqrcodesheet.pdf`,
  SITE_USER_RESEND_INVITE: (siteId, userId) =>
    `${API_URL}/sites/${siteId}/users/${userId}/resendinvite`,
  SITE_USER_RESEND_INVITE_ALL: (siteId) =>
    `${API_URL}/sites/${siteId}/users/resendinvite`,
  SITE_USER_USER_GROUPS: (sid, uid) =>
    `${API_URL}/sites/${sid}/users/${uid}/usergroups`, // GET
  SITE_USERS: (id) => `${API_URL}/sites/${id}/users`,
  SITE_USERS_SLIM: (id) => `${API_URL}/sites/${id}/users?slim=true`,
  STRIPE_CANCEL: `${API_URL}/stripe/cancel`, // POST
  STRIPE_COUPON: (couponCode) => `${API_URL}/stripe/coupons/${couponCode}`,
  STRIPE_PAYMENT_METHODS: (id) => `${API_URL}/stripe/${id}/sources`, // GET
  STRIPE_PRORATE_PREVIEW: `${API_URL}/stripe/prorate`, // POST
  STRIPE_UPDATE_PAYMENT_METHOD: `${API_URL}/stripe/update`, // POST
  STRIPE_UPGRADE: `${API_URL}/stripe/upgrade`, // POST
  SUPERVISOR: (id) => `${API_URL}/sites/${id}/supervisor`,
  TAGS: (siteId) => `${API_URL}/reservables/tags?site_id=${siteId}`, // GET
  TAGS_CREATE: `${API_URL}/reservables/tags`, // POST
  TAGS_DELETE: `${API_URL}/reservables/tags/delete`, // POST
  TAGS_MODIFY: `${API_URL}/reservables/tags/modify`, // POST
  TAGS_NO_PARAMS: `${API_URL}/reservables/tags`, // GET
  THEME: (id) => `${API_URL}/themes/${id}`, // GET
  THEME_AUTOJOIN: (id) => `${API_URL}/themes/${id}/autojoin`, // POST
  THEME_CREATE: `${API_URL}/themes`, // POST
  THEME_DELETE: (id) => `${API_URL}/themes/${id}/delete`, // POST
  THEME_IMAGE: (id) => `${API_URL}/themes/${id}/image`, // POST image
  THEME_IMAGE_FULL: (id) => `${API_URL}/themes/${id}/image`, // GET image
  THEME_IMAGE_THUMBNAIL: (id) =>
    `${API_URL}/themes/${id}/image/thumbnail`, // GET image
  TIME_SERVER: `${API_URL}/thetime`,
  TRAINING_GENERATE_BULK_TEMPLATE: `${API_URL}/credentials/generatebulk`, // GET
  TRAINING_GENERATE_BULK_FOR_PORTAL: (id) =>
    `${API_URL}/credentials/generatebulk/portal/${id}`, // POST
  TRAINING_GENERATE_BULK_FOR_SITE: (id) =>
    `${API_URL}/credentials/generatebulk/site/${id}`, // POST
  TRAINING_PORTAL_MODULES: `${API_URL}/portals/training`, // GET
  TRAINING_RECORD_ADD: (id) => `${API_URL}/training/record_add/${id}`, // POST
  TRAINING_RECORD_DELETE: (id) =>
    `${API_URL}/training/record/${id}/delete`, // POST
  TRAINING_RECORD_EXPIRE: (id) =>
    `${API_URL}/training/record/${id}/expire`, // POST
  TRAINING_RECORD_EXPIRE_BULK:
    `${API_URL}/training/record_expire`, // POST
  TRAINING_RECORDS: `${API_URL}/training/records`,
  TRAINING_RECORDS_COUNT: (id) =>
    `${API_URL}/training/${id}/records/count`, // GET
  TRAINING_RECORDS_CSV: `${API_URL}/training/records/csv`,
  TRAINING_RECORDS_CSV_FROM_TOKEN: (token) =>
    `${API_URL}/training/records/csv/${token}`,
  TRAINING_RECORDS_USER: (id) => `${API_URL}/training/records?user_id=${id}`,
  TRAINING_RECORDS_EXPIRING: (id) => `${API_URL}/training/records?user_id=` +
    `${id}&expiring=t`,
  TRAINING_RECORDS_HISTORY: (id, idx, l) => `${API_URL}/training/records?` +
    `user_id=${id}&offset=${idx}&limit=${l}&return_count=true`,
  TRAINING_SITE_MODULE_EDIT: `${API_URL}/sites/training`, // POST
  TRAINING_SITE_MODULES: (id) =>
    `${API_URL}/sites/${id}/training?active=true`, // GET
  TRAINING_SITE_MODULES_INACTIVE: (id) =>
    `${API_URL}/sites/${id}/training?inactive=true`, // GET
  UNSUBSCRIBE: `${API_URL}/mail/unsubscribe`, // POST
  USER: (id) => `${API_URL}/users/${id}`,
  USER_CONTACT: (id) => `${API_URL}/users/${id}/sendmessage`, // POST
  USER_DELETE: (id) => `${API_URL}/users/${id}/deactivate`, // POST
  USER_IMAGE_DELETE: (id) =>
    `${API_URL}/users/${id}/image/delete`, // POST
  USER_IMAGE_FULL: (id) =>
    `${API_URL}/users/${id}/image/full`, // GET
  USER_IMAGE_FULL_URL: (id) =>
    `${API_URL}/users/${id}/image/full/url`, // GET
  USER_IMAGE_THUMBNAIL: (id) =>
    `${API_URL}/users/${id}/image/thumbnail`, // GET
  USER_IMAGE_THUMBNAIL_URL: (id) =>
    `${API_URL}/users/${id}/image/thumbnail/url`, // GET
  USER_EMAIL_ADD: `${API_URL}/addemail`, // POST
  USER_EMAIL_DELETE: `${API_URL}/removeemail`, // POST
  USER_GROUP: (sId, ugId) => `${API_URL}/sites/${sId}/usergroup/${ugId}`, // GET
  USER_GROUPS: (id) => `${API_URL}/sites/${id}/usergroups`, // GET or POST
  USER_GROUPS_NO_USERS: (id) =>
    `${API_URL}/sites/${id}/usergroups?inc_users=f`, // GET
  USER_GROUP_DELETE: (sid, gid) =>
    `${API_URL}/sites/${sid}/usergroups/${gid}/delete`, // POST
  USER_GROUP_MODIFY: (sid, gid) =>
    `${API_URL}/sites/${sid}/usergroups/${gid}`, // POST
  USER_GROUPS_PORTAL: (id) => `${API_URL}/portalsiteusergroups/${id}`,
  USER_GROUPS_SEARCH: `${API_URL}/sites/usergroups`, // GET
  USER_ICS_REGENERATE: `${API_URL}/users/self/regenerateics`, // POST
  USER_MESSAGES: (idx, l) => `${API_URL}/users/messages?` +
    `offset=${idx}&limit=${l}`, // GET
  USER_PASSWORD_RESET: `${API_URL}/initiatepasswordreset`, // POST
  USER_PARTNER_DELETE: (id) => `${API_URL}/users/${id}/modify/delete`, // POST
  USER_PIN_REGENERATE: `${API_URL}/users/self/regeneratepin`, // POST
  USER_TOTP_CLEAR: `${API_URL}/users/totp/clear`, // POST
  USER_TOTP_USE_AND_EXPIRE_RECOVERY_CODE: (email) =>
    `${API_URL}/users/${email}/totp/recoverycode`,
  USER_TOTP_QR_CODE: (token) => `${API_URL}/users/totpqrcode?` +
    `qauthtoken=${token}&t=${(new Date()).getTime()}`, // GET image
  USER_TOTP_QR_CODE_GENERATE: `${API_URL}/users/totpqrcode`, // POST
  USER_TOTP_REGISTER: `${API_URL}/users/totpqrcode/activate`, // POST
  USER_SEARCH: `${API_URL}/users`, // GET
  USER_SUBSCRIPTIONS: (id) => `${API_URL}/users/${id}/subscriptions`, // POST
  USER_UPDATE: (id) => `${API_URL}/users/${id}`, // POST
  USER_UPDATE_IMAGE: (id) => `${API_URL}/users/${id}/image`, // POST
  USERS: `${API_URL}/users`, // GET
  USERS_BULK_MESSAGE: `${API_URL}/users/bulkmessage`, // POST
  USERS_CSV_DOWNLOAD: (token) => `${API_URL}/users/csv/${token}`, // GET
  USERS_PORTAL_CSV_TOKEN: (portalId) =>
    `${API_URL}/users.csv?portal_id=${portalId}`, // GET
  USERS_SITE_CSV_TOKEN: (siteId) =>
    `${API_URL}/users.csv?site_id=${siteId}`, // GET
  USERS_GENERATE_BULK_FOR_SITE: (id) =>
    `${API_URL}/upload/users/generatebulk/site/${id}`, // POST
  USERS_GENERATE_BULK_FOR_PORTAL: (id) =>
    `${API_URL}/upload/users/generatebulk/portal/${id}`, // POST
  USERS_GENERATE_BULK_EXAMPLE: `${API_URL}/users/generatebulk/template`, // GET
  USERS_GENERATE_BULK_EXAMPLE_FOR_SITE:
    (id, token) => `${API_URL}/users/generatebulk/template/site/${id}` +
    `?qauthtoken=${token}&t=${(new Date()).getTime()}`, // GET
  USERS_GENERATE_BULK_EXAMPLE_FOR_PORTAL:
    (id, token) => `${API_URL}/users/generatebulk/template/portal/${id}` +
    `?qauthtoken=${token}&t=${(new Date()).getTime()}`, // GET
  USERS_QRID: (qrid) => `${API_URL}/users/qrid/${qrid}`, // GET
  VERIFY_APPROVER: `${API_URL}/verifyapprover`, // POST
  VERIFY_EMAIL: `${API_URL}/verifyemail`, // POST
  VERIFY_PUBLIC_EMAIL: `${API_URL}/verifypublicemail`, // POST
  VERIFY_SUPERVISOR: `${API_URL}/verifysupervisor`, // POST
  WEEKLY_EVENT_DELETE: (id) => `${API_URL}/weeklyevent/${id}/delete`, // POST
  WEBAUTHN_DEVICE_LIST: `${API_URL}/2fa/webauthn/devices`,
  WEBAUTHN_REG_CHALLENGE: `${API_URL}/2fa/webauthn/register/challenge`,
  WEBAUTHN_REG_VERIFY: `${API_URL}/2fa/webauthn/register/verify`,
  WEBAUTHN_DEACTIVATE: `${API_URL}/2fa/webauthn/revoke`,
  WEEKLY_SLOT_ADD: (id) =>
    `${API_URL}/reservables/${id}/weeklyevent/slot`, // POST
  WEEKLY_SLOTS_CLEAR: `${API_URL}/weeklyevent/weeklyevent/delete/bulk`, // POST
  WISHLIST_RESERVABLE_CREATE: `${API_URL}/wishlist/wish/create`, // POST
  WISHLIST_RESERVABLES: `${API_URL}/wishlist/wishes`, // GET
  WISHLIST_USER_EDIT: `${API_URL}/wishlist/edituser`, // POST

  // ---------------------------------------------------------------------------
  // Websockets
  // ---------------------------------------------------------------------------
  DEV_LIVE_SUBSCRIBE: `${API_URL}/admin/ws/subscribe/live`,
  WEBSOCKET: WEBSOCKET_URL,
};

export const params = {
  RESERVATION_BOOK_ATTRIBS: 'name+reservable_id+can_reserve+rates+loanable+' +
    'future_loans+can_make_recurring_reservations+reservable+' +
    'rates+reservation_max_duration+anyapprovers+approvers+is_addon+' +
    'units+site_id+bgcolor+icsurl+active+buffer_before+blocks+disclaimer+' +
    'reservation_unit_limit+loan_duration+reservable_type+rate_quotes+' +
    'currency+autocancel_window+autodeny_window+reservation_forms+' +
    'show_account_number_on_reservations+units_name+units_mode+' +
    'show_purpose_on_reservations+show_note_on_reservations+' +
    'show_name_override_on_reservations+weekly_blocks_mode+' +
    'accounts_debit+accounts_sufficient_balance_required+' +
    'units_a_f_t_id+units_account_applied_id+category_sortable+' +
    'creation_start_time_restriction+status+status_detail+' +
    'creation_start_time_restriction_future+restrict_start_end_minutes+' +
    'blackouts+default_reservation_duration+reservation_minimum+image_icon+' +
    'image_is_default+nolist_to_cannot_reserve_users+related_reservables+' +
    'is_addon_by_related+can_only_reserve_with_related+has_weekly_slots+' +
    'custom_reserve_button_text+barcode+events+allow_accessory_users+' +
    'autocancel_invoiced_automatically_window+restricted_to_templates_only+' +
    'reservable_site_ordinal_id+rate_name_only+default_calendar_view+' +
    'maintenance_forms+skip_global_forms+exclude_from_site_calendar+' +
    'can_be_included_in_reservations+rate_quotes_require_account_number',
  RESERVATION_BOOK_TIMES_ATTRIB: 'reservable_id+start_times_common',
};
